/**
 * Shared types of the spreadsheet grid (pure; no React). The grid works on plain row objects;
 * every column knows how to read, show and parse its value.
 */

/** Raw value of a grid cell. Arrays are used by multi-select cells (e.g. mix outputs). */
export type GridValue = string | number | boolean | readonly string[];

export type ColumnType =
  'text' | 'number' | 'boolean' | 'select' | 'multiselect' | 'autocomplete' | 'send';

export interface GridOption {
  value: string;
  label: string;
  hint?: string;
  /** Icon key (`stand:<type>`, `share`, U68), rendered before the label. */
  icon?: string;
  /** Heading of the option group; a new heading starts where it changes. */
  group?: string;
}

export interface GridColumn<R> {
  key: string;
  header: string;
  type: ColumnType;
  /** Width in px (default by type). */
  width?: number;
  value(row: R): GridValue;
  /** Display / copy text (default: {@link formatValue}). */
  format?(value: GridValue, row: R): string;
  /** Parses typed or pasted text; undefined = invalid (the edit is ignored). */
  parse?(text: string, row: R | undefined): GridValue | undefined;
  /** Default false. */
  editable?: boolean | ((row: R) => boolean);
  /** Options of select / multiselect cells. */
  options?: readonly GridOption[] | ((row: R | undefined) => readonly GridOption[]);
  /** Suggestions of autocomplete cells. */
  suggest?(query: string, row: R | undefined): readonly GridOption[];
  align?: 'start' | 'end';
  /** Extra class for the cell (e.g. informal values). */
  cellClass?(row: R): string | undefined;
  /** Tooltip of a cell. */
  title?(row: R): string | undefined;
  /** Icon key shown before the cell text (see {@link GridOption.icon}). */
  icon?(row: R): string | undefined;
}

/** One edited cell of an existing row. */
export interface CellChange {
  rowKey: string;
  column: string;
  value: GridValue;
}

/** Result of an edit gesture: cell changes plus rows appended at the end (paste / new-row line). */
export interface GridEdit {
  changes: CellChange[];
  /** Values per column key of each new row, in order. */
  appended: Record<string, GridValue>[];
}

export function emptyEdit(): GridEdit {
  return { changes: [], appended: [] };
}

export function isEmptyEdit(e: GridEdit): boolean {
  return e.changes.length === 0 && e.appended.length === 0;
}
